import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography
} from '@mui/material';
import { createFakeServer } from '@mui/x-data-grid-generator';
import {
  GridFooter,
  GridFooterContainer,
  GridRowId,
  gridVisibleColumnDefinitionsSelector,
  gridVisibleSortedRowIdsSelector,
  useGridApiRef
} from '@mui/x-data-grid-pro';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import Loader from 'src/components/common/Loader';
import { PopupComponent } from 'src/components/common/PopupComponent';
import { usePermissionWrapper } from 'src/components/hooks/usePermissionWrapper';
import { useFieldTranslation } from 'src/components/fields/useFieldTranslation';
import { useUser } from 'src/components/hooks/useUser';
import { archLevel } from 'src/data/config';
import { useGetAssigneesMutation } from 'src/services/Utils';
import { useGetCodeListDataQuery } from 'src/services/codeList';
import { hideLoader, showLoader } from 'src/store/Loader';
import { setMessage } from 'src/store/Message';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import '../studylogStyles.scss';
import AddIssueForm from './AddIssue';
import DataGridIssueLog from './DataGridIssueLog';
import { useIssueLog } from './useIssueLog';

const Issues = ({ setStudyLogOpen, setIssuesOn }: any) => {
  const { checkPermission } = usePermissionWrapper();
  const dispatchApp = useAppDispatch();
  const apiRef = useGridApiRef();
  //site level permission //
  const viewSiteIssuePermission = checkPermission('view_site_issue');
  const updateSiteIssuePermission = checkPermission('update_site_issue');
  const updateSiteActionPermission = checkPermission('update_site_issue_action');
  const updateSiteDecisionPermission = checkPermission('update_site_issue_decision');
  const updateOtherSitePermission = checkPermission('update_other_site_issue');
  const deleteOtherSitePermission = checkPermission('delete_other_site_issue');
  // Sponsor level permission
  const viewSponsorIssuePermission = checkPermission('view_sponsor_issue');
  const updateSponsorIssuePermission = checkPermission('update_sponsor_issue');
  const updateSponsorActionPermission = checkPermission('update_sponsor_issue_action');
  const updateSponsorDecisionPermission = checkPermission('update_sponsor_issue_decision');
  const updateOtherSponsorPermission = checkPermission('update_other_sponsor_issue');
  const deleteOtherSponsorPermission = checkPermission('delete_other_sponsor_issue');
  // copy to sponsor issue permission //
  const copyToSponsorIssuePermission = checkPermission('copy_as_new_sponsorissue');
  // state for maintaining permision//
  const [issueLogPermissions, setIssueLogPermissions] = useState<any>({});
  const coordinates = {
    rowIndex: 0,
    colIndex: 0
  };

  const { user, email } = useUser();
  const currentUserWithEmail = `${user} (${email})`;
  const componentRef = useRef<any>(null);
  const buttonRef = useRef<any>();
  const [rowSelection, setRowSelection] = useState<any>([]);
  const [deleteData, setDeleteData] = useState<any>({});
  const [edit, setEdit] = useState(false);
  const [rows, setRows] = useState<any>([]);
  const [submit, setSubmit] = useState(false);
  const [value, setValue] = React.useState<number>(0);
  const [currentIssueLevel, setIssueLevel] = React.useState<string>('');
  const studyId: any = useAppSelector(state => state.site.selectedStudy);
  const selectedSite = useAppSelector(state => state.site.selectedSite);
  const selectedCountry = useAppSelector(state => state.site.selectedCountry);
  const currentHierarchyLevel = useAppSelector(state => state.site.currentHierarchyLevel);
  const isHierarchyLevelClosed: boolean = useAppSelector(state => state.site.isHierarchyLevelClosed);
  const initialState = {
    created_by: user,
    study_id: studyId,
    site_id: selectedSite,
    country: selectedCountry,
    issue_level: currentIssueLevel,
    application: 'CTMS',
    action_assigned_to: [],
    decision_assigned_to: []
  };
  const [openCopyToSponsor, setOpenCopyToSponsor] = useState<boolean>(false);
  const {
    copyToSponsor,
    copyToSponsorData,
    copyToSponsorError,
    copyToSponsorIsError,
    copyToSponsorIsLoading,
    copyToSponsorIsSuccess
  } = useIssueLog();
  const { unSavedWarning } = useFieldTranslation();
  const [category, setCategory] = useState([]);
  const [issue, setIssue] = useState([]);
  const [serverity, setServerity] = useState([]);
  const [openConfirmExit, setOpenConfirmExit] = useState(false);
  const [warningMessage, setWarningMessage] = useState(false);
  const { data: stICCATData } = useGetCodeListDataQuery('STICCAT');
  const { data: stISSEVData } = useGetCodeListDataQuery('STISSEV');
  const { data: stISCLSData } = useGetCodeListDataQuery('STISCLS');
  const [columnVisibilityModelSite, setColumnVisibilityModelSite] = useState({
    id: false,
    isSelected: false
  });
  const [columnVisibilityModelSponsor, setColumnVisibilityModelSponsor] = useState({
    id: false,
    isSelected: false
  });
  const [dataGridShow, setDataGridShow] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [rowsCount, setRowsCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(0);
  const [filterObject, setFilterObject] = useState<any>();

  useEffect(() => {
    if (archLevel == 'site' && archLevel != 'sponsor' && viewSiteIssuePermission) {
      setIssueLevel('SITE');
    } else if (archLevel == 'sponsor' && archLevel != 'site' && viewSiteIssuePermission) {
      setIssueLevel('SITE');
    } else if (archLevel == 'sponsor' && archLevel != 'site' && viewSponsorIssuePermission) {
      setIssueLevel('SPONSOR');
    }
  }, []);

  useEffect(() => {
    if (stISCLSData) {
      setIssue(stISCLSData);
    }
    if (stISSEVData) {
      /** sorting Severity list in descending order */
      const sortedData = stISSEVData?.slice()?.sort((a: any, b: any) => {
        return b.code_list_value_code - a.code_list_value_code;
      });
      setServerity(sortedData);
    }
    if (stICCATData) {
      setCategory(stICCATData);
    }
  }, [stISCLSData, stISSEVData, stICCATData]);
  const [selectionModel, setSelectionModel] = useState([]);
  const [createData, setCreateData] = useState<any>(initialState);

  const [updateData, setUpdateData] = useState<any>({
    updated_by: user,
    application: 'CTMS',
    action_assigned_to: [],
    decision_assigned_to: [],
    action_completion_dt: '',
    action_taken: '',
    dt_occur: '',
    issue_no: '',
    issue_id: '',
    decision_update: '',
    decision_comp_dt: '',
    id: ''
  });
  const [disableSubmit, setDisableSubmit] = useState<boolean>(false);

  const {
    getIssueLogData,
    issueLogData,
    issueLogDataError,
    issueLogDataIsError,
    issueLogDataIsLoading,
    createIssueLog,
    createIssueLogIsSuccess,
    createIssueLogIsLoading,
    createIssueLogIsError,
    createIssueLogError,
    updateIssueLog,
    updateIssueLogError,
    updateIssueLogIsError,
    updateIssueLogIsLoading,
    updateIssueLogIsSuccess,
    updateIssueAction,
    updateIssueActionIsSuccess,
    updateIssueActionIsError,
    updateIssueActionError,
    updateIssueActionIsLoading,
    deleteIssueLog,
    deleteIssueLogData,
    deleteIssueLogError,
    deleteIssueLogIsError,
    deleteIssueLogIsLoading,
    deleteIssueLogIsSuccess,
    updateIssueDesision,
    updateIssueDesisionError,
    updateIssueDesisionIsError,
    updateIssueDesisionIsLoading,
    updateIssueDesisionIsSuccess,
    deleteAllIssueLog,
    deleteAllIssueLogData,
    deleteAllIssueLogError,
    deleteAllIssueLogIsError,
    deleteAllIssueLogIsLoading,
    deleteAllIssueLogIsSuccess,
    updateAllIssueLog,
    updateAllIssueLogError,
    updateAllIssueLogIsError,
    updateAllIssueLogIsLoading,
    updateAllIssueLogIsSuccess
  } = useIssueLog();
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (currentIssueLevel === 'SITE') {
      setIssueLogPermissions({
        view: viewSiteIssuePermission,
        update: updateSiteIssuePermission,
        updateOther: updateOtherSitePermission,
        updateAction: updateSiteActionPermission,
        updateDecision: updateSiteDecisionPermission,
        deleteOther: deleteOtherSitePermission
      });
    } else {
      setIssueLogPermissions({
        view: viewSponsorIssuePermission,
        update: updateSponsorIssuePermission,
        updateOther: updateOtherSponsorPermission,
        updateAction: updateSponsorActionPermission,
        updateDecision: updateSponsorDecisionPermission,
        deleteOther: deleteOtherSponsorPermission
      });
    }
  }, [currentIssueLevel]);

  const getIssueLog = async (
    studyId: string | null,
    selectedCountry: string | null,
    selectedSite: string | null,
    issue_level: string,
    from: number,
    size: number
  ) => {
    setDataGridShow(true);
    if (currentIssueLevel == '') {
      return;
    }
    const tempIssueLog: any = await getIssueLogData({
      data: { ...filterObject },
      site_id: selectedSite,
      study_id: studyId,
      country: selectedCountry,
      issue_level: currentIssueLevel,
      application: archLevel === 'site' ? process.env.REACT_APP_TSDA_SLUG : process.env.REACT_APP_NAME,
      from,
      size
    });
    // setting rows from the response when there is data
    if (tempIssueLog?.isSuccess) {
      setRowsCount(tempIssueLog?.data.record_count);
      const tempRows: any = [];
      tempIssueLog?.data &&
        tempIssueLog?.data.selected_data.length > 0 &&
        tempIssueLog?.data.selected_data.map((issuesData: any) => {
          const data = Object.assign({}, issuesData.issue_details);
          // updated the data based on true and false values
          data.action_req = data?.action_req ? 'Yes' : 'No';
          data.decision_req = data?.decision_req ? 'Yes' : 'No';
          tempRows.push({
            ...data,
            study_id: issuesData.study_id,
            country: issuesData.country,
            site_id: issuesData.site_id,
            issue_id: data.id,
            action_assigned_to: issuesData.action_assigned_to !== null ? issuesData.action_assigned_to : [],
            decision_assigned_to: issuesData.decision_assigned_to !== null ? issuesData.decision_assigned_to : []
          });
        });
      if (tempRows.length > 0) {
        setRows(tempRows);
        const { rowIndex, colIndex } = coordinates;
        apiRef?.current?.scrollToIndexes(coordinates);
        const id = gridVisibleSortedRowIdsSelector(apiRef)[rowIndex];
        const column = gridVisibleColumnDefinitionsSelector(apiRef)[colIndex];
        apiRef?.current?.setCellFocus(id, column.field);
      }
    } else if (tempIssueLog?.isError) {
      setRowsCount(0);
      setPageNumber(0);
      setRows([]);
    }
    setDataGridShow(false);
  };
  const getAllIssueLogs = (from = -1, size = -1) => {
    if (currentHierarchyLevel === 'site' && !isHierarchyLevelClosed) {
      getIssueLog(studyId, selectedCountry, selectedSite, currentIssueLevel, from, size);
      // removed heirarchy
    } else {
      getIssueLog(
        currentHierarchyLevel !== null ? studyId : null,
        currentHierarchyLevel !== 'study' && currentHierarchyLevel !== null ? selectedCountry : null,
        currentHierarchyLevel === 'site' ? selectedSite : null,
        currentIssueLevel,
        from,
        size
      );
    }
  };
  useEffect(() => {
    getAllIssueLogs(0, 10);
  }, [currentHierarchyLevel, studyId, selectedCountry, selectedSite, value, currentIssueLevel]);
  useEffect(() => {
    if (!issueLogDataIsLoading && (pageSize === 10 || pageSize === 20 || pageSize === 30)) {
      if (pageNumber * pageSize > rowsCount - 1) {
        setPageNumber(0);
        getAllIssueLogs(0, pageSize);
      } else {
        getAllIssueLogs(pageNumber * pageSize, pageSize);
      }
    }
  }, [pageNumber, pageSize, filterObject]);
  useEffect(() => {
    if (issueLogDataIsError) {
      // setting protocol and sponsor details
      if (!issueLogData) {
        setRows([]);
      }

      const err: any = issueLogDataError;
      if (err?.status == 500) {
        dispatch(
          setMessage({
            msgDetails: `${err?.status} : Internal Server Error`,
            severity: 'error'
          })
        );
      } else if (err?.status == 403) {
        dispatch(
          setMessage({
            msgDetails: 'Error 403 Restricted User permission',
            severity: 'error'
          })
        );
      }
    }
  }, [issueLogDataIsError]);
  useEffect(() => {
    if (
      updateIssueDesisionIsLoading ||
      deleteIssueLogIsLoading ||
      deleteAllIssueLogIsLoading ||
      createIssueLogIsLoading ||
      updateIssueLogIsLoading ||
      updateAllIssueLogIsLoading ||
      updateIssueActionIsLoading
    ) {
      dispatch(showLoader());
    } else {
      dispatch(hideLoader());
    }
  }, [
    updateIssueDesisionIsLoading,
    deleteIssueLogIsLoading,
    deleteAllIssueLogIsLoading,
    createIssueLogIsLoading,
    updateIssueLogIsLoading,
    updateAllIssueLogIsLoading,
    updateIssueActionIsLoading
  ]);
  // For Add Issue Dialog
  const [open, setOpen] = React.useState(false);
  const [openDelet, setOpenDelete] = useState(false);
  const [error, setError] = useState(false);
  const [atError, setAtError] = useState(false);
  const [daError, setDaError] = useState(false);
  const [duError, setDuError] = useState(false);
  const [ddError, setDdError] = useState(false);
  const [iaError, setIaError] = useState(false);
  const { study_id: studyIdVal, country, site_id: siteIdVal, ...createDataRest } = createData;

  const handleClickOpen = () => {
    setEdit(false);
    setCreateData(initialState);
    setOpen(true);
  };

  const handleClose = () => {
    if (warningMessage) {
      setOpenConfirmExit(true);
    } else {
      setSubmit(false);
      setError(false);
      setAtError(false);
      setDaError(false);
      setDuError(false);
      setDdError(false);
      setOpen(false);
    }
  };
  let createRequestObj = {};
  const handleSave = async () => {
    dispatch(showLoader());
    buttonRef.current.setAttribute('disabled', 'disabled');
    setDisableSubmit(true);
    setWarningMessage(false);
    setSubmit(true);
    if (edit) {
      if (
        (issueLogPermissions.update || issueLogPermissions.updateOther) &&
        updateData.category &&
        updateData.severity &&
        updateData.issue_class &&
        updateData.dt_occur &&
        updateData.issue &&
        updateData.dt_report &&
        (updateData.reported_irb === 'Yes' || updateData.reported_irb === true ? updateData.date_irb : true) &&
        (updateData.action_req ? updateData.action_req : true) &&
        (updateData.action_taken ? updateData.action_completion_dt : true) &&
        (updateData.decision_update ? updateData.decision_comp_dt : true) &&
        (updateData.action_completion_dt ? updateData.action_taken : true) &&
        (updateData.decision_comp_dt ? updateData.decision_update : true)
      ) {
        if (updateData.dt_occur > updateData.dt_report) {
          setError(true);
          dispatch(
            setMessage({
              msgDetails: 'Date of Issue Reporting cannot be less than Date of Issue Occurrence',
              severity: 'error'
            })
          );
        } else if (updateData.action_completion_dt && updateData.action_completion_dt < updateData.dt_occur) {
          setIaError(true);
          setDaError(true);
          dispatch(
            setMessage({
              msgDetails: 'Date of Action Completion cannot be lesser than Issue occurrence date',
              severity: 'error'
            })
          );
        } else if (updateData.decision_comp_dt && updateData.decision_comp_dt < updateData.dt_occur) {
          setIaError(true);
          setDdError(true);
          dispatch(
            setMessage({
              msgDetails: 'Date of Decision completion cannot be lesser than Issue occurrence date',
              severity: 'error'
            })
          );
          componentRef.current.focus();
        } else {
          if (updateData.created_by === user) {
            // update the issue and refetching the getIssueLog
            await updateIssueLog({ data: updateData });
            getIssueLog(studyId, selectedCountry, selectedSite, currentIssueLevel, pageNumber * pageSize, pageSize);
          } else if (issueLogPermissions.updateOther) {
            // update the issue and refetching the getIssueLog
            await updateAllIssueLog({ data: updateData });
            getIssueLog(studyId, selectedCountry, selectedSite, currentIssueLevel, pageNumber * pageSize, pageSize);
          } else {
            const actionUser =
              updateData.action_assigned_to.length <= 0
                ? updateData.action_assigned_to
                : updateData.action_assigned_to.filter((name: string) => name === currentUserWithEmail);
            const decisionUser =
              updateData.decision_assigned_to.length <= 0
                ? updateData.decision_assigned_to
                : updateData.decision_assigned_to.filter((name: string) => name === currentUserWithEmail);
            updateData.action_req == 'Yes' && actionUser.length > 0 && handleIssueAction();
            updateData.decision_req == 'Yes' && decisionUser.length > 0 && handleIssueDecision();
            getIssueLog(studyId, selectedCountry, selectedSite, currentIssueLevel, pageNumber * pageSize, pageSize);
          }
        }
      } else {
        //checking Assined user for Action or Decision
        const actionUser =
          updateData.action_assigned_to.length <= 0
            ? updateData.action_assigned_to
            : updateData.action_assigned_to.filter((name: string) => name === currentUserWithEmail);
        const decisionUser =
          updateData.decision_assigned_to.length <= 0
            ? updateData.decision_assigned_to
            : updateData.decision_assigned_to.filter((name: string) => name === currentUserWithEmail);

        if (
          issueLogPermissions.updateAction &&
          actionUser.length > 0 &&
          issueLogPermissions.updateDecision &&
          decisionUser.length > 0
        ) {
          handleActionAndDecision();
        } else if (issueLogPermissions.updateAction && actionUser.length > 0) {
          handleIssueAction();
        } else if (issueLogPermissions.updateDecision && decisionUser.length > 0) {
          handleIssueDecision();
        }
      }
    } else {
      if (
        createData.study_id &&
        createData.category &&
        createData.severity &&
        createData.issue_class &&
        createData.dt_occur &&
        createData.issue &&
        createData.dt_report &&
        (createData.reported_irb === 'Yes' || createData.reported_irb === true ? createData.date_irb : true) &&
        (createData.action_req ? createData.action_req : true) &&
        (createData.action_taken ? createData.action_completion_dt : true) &&
        (createData.decision_update ? createData.decision_comp_dt : true) &&
        (createData.action_completion_dt ? createData.action_taken : true) &&
        (createData.decision_comp_dt ? createData.decision_update : true)
      ) {
        if (createData.dt_occur > createData.dt_report) {
          setError(true);
          dispatch(
            setMessage({
              msgDetails: 'Date of Issue Reporting cannot be less than Date of Issue Occurrence',
              severity: 'error'
            })
          );
        } else if (createData.action_completion_dt && createData.action_completion_dt < createData.dt_occur) {
          dispatch(
            setMessage({
              msgDetails: 'Date of Action Completion cannot be lesser than Issue occurrence date',
              severity: 'error'
            })
          );
        } else if (createData.decision_comp_dt && createData.decision_comp_dt < createData.dt_occur) {
          dispatch(
            setMessage({
              msgDetails: 'Date of Decision completion cannot be lesser than Issue occurrence date',
              severity: 'error'
            })
          );
        } else {
          // update the issue and refetching the getIssueLog
          if (createRequestObj !== createDataRest) {
            createRequestObj = createDataRest;
            createIssueLog({
              data: createDataRest,
              study_id: createData.study_id,
              country: createData.country,
              site_id: createData.site_id
            });
          }
        }
      }
    }
    dispatch(hideLoader());
  };
  const handleIssueAction = async () => {
    if (
      updateData.action_completion_dt === null ||
      updateData.action_completion_dt === '' ||
      updateData.action_taken === null ||
      updateData.action_taken === ''
    ) {
      setAtError(true);
      setDaError(true);
      dispatch(
        setMessage({
          msgDetails: 'Date of Action Completion/Action Taken is blank',
          severity: 'error'
        })
      );
    } else if (updateData.action_completion_dt > new Date().toLocaleDateString('en-CA')) {
      dispatch(
        setMessage({
          msgDetails: 'Invalid or Future date entered',
          severity: 'error'
        })
      );
    } else if (updateData.action_completion_dt < updateData.dt_occur) {
      setIaError(true);
      setDaError(true);
      dispatch(
        setMessage({
          msgDetails: 'Date of Action Completion cannot be lesser than Issue occurrence date',
          severity: 'error'
        })
      );
    } else {
      await updateIssueAction({
        issue_id: updateData.issue_id,
        site_id: selectedSite,
        study_id: studyId,
        country: selectedCountry,
        application: 'CTMS',
        issue_level: currentIssueLevel,
        data: { action_taken: updateData.action_taken, action_completion_date: updateData.action_completion_dt }
      });
      setOpen(false);
    }
  };
  const handleIssueDecision = async () => {
    if (
      updateData.decision_comp_dt === null ||
      updateData.decision_comp_dt === '' ||
      updateData.decision_update === ''
    ) {
      setDuError(true);
      setDdError(true);
      dispatch(
        setMessage({
          msgDetails: 'Decision Update/Date of Decision Completion is blank',
          severity: 'error'
        })
      );
    } else if (updateData.decision_comp_dt > new Date().toLocaleDateString('en-CA')) {
      dispatch(
        setMessage({
          msgDetails: 'Invalid or Future date entered',
          severity: 'error'
        })
      );
    } else if (updateData.decision_comp_dt < updateData.dt_occur) {
      setIaError(true);
      setDdError(true);
      dispatch(
        setMessage({
          msgDetails: 'Date of Decision completion cannot be lesser than Issue occurrence date',
          severity: 'error'
        })
      );
      componentRef.current.focus();
    } else {
      await updateIssueDesision({
        issue_id: updateData.id,
        site_id: selectedSite,
        study_id: studyId,
        country: selectedCountry,
        application: 'CTMS',
        issue_level: currentIssueLevel,
        data: { decision_update: updateData.decision_update, decision_completion_date: updateData.decision_comp_dt }
      });
      setOpen(false);
    }
  };

  const handleActionAndDecision = async () => {
    if (
      updateData.action_req === 'Yes' &&
      (updateData.action_completion_dt === null ||
        updateData.action_completion_dt === '' ||
        updateData.action_taken === null ||
        updateData.action_taken === '')
    ) {
      setAtError(true);
      setDaError(true);
      dispatch(
        setMessage({
          msgDetails: 'Date of Action Completion/Action Taken is blank',
          severity: 'error'
        })
      );
    } else if (updateData.action_req === 'Yes' && updateData.action_completion_dt < updateData.dt_occur) {
      setIaError(true);
      setDaError(true);
      dispatch(
        setMessage({
          msgDetails: 'Date of Action Completion cannot be lesser than Issue occurrence date',
          severity: 'error'
        })
      );
    } else if (
      updateData.decision_req === 'Yes' &&
      (updateData.decision_comp_dt === null || updateData.decision_comp_dt === '' || updateData.decision_update === '')
    ) {
      setDuError(true);
      setDdError(true);
      dispatch(
        setMessage({
          msgDetails: 'Decision Update/Date of Decision Completion is blank',
          severity: 'error'
        })
      );
    } else if (
      updateData.action_completion_dt > new Date().toLocaleDateString('en-CA') ||
      updateData.decision_comp_dt > new Date().toLocaleDateString('en-CA')
    ) {
      dispatch(
        setMessage({
          msgDetails: 'Invalid or Future date entered',
          severity: 'error'
        })
      );
    } else if (updateData.decision_req === 'Yes' && updateData.decision_comp_dt < updateData.dt_occur) {
      setIaError(true);
      setDdError(true);
      dispatch(
        setMessage({
          msgDetails: 'Date of Decision completion cannot be lesser than Issue occurrence date',
          severity: 'error'
        })
      );
      componentRef.current.focus();
    } else {
      await updateIssueAction({
        issue_id: updateData.issue_id,
        site_id: selectedSite,
        study_id: studyId,
        country: selectedCountry,
        application: 'CTMS',
        issue_level: currentIssueLevel,
        data: { action_taken: updateData.action_taken, action_completion_date: updateData.action_completion_dt }
      });
      await updateIssueDesision({
        issue_id: updateData.id,
        site_id: selectedSite,
        study_id: studyId,
        country: selectedCountry,
        application: 'CTMS',
        issue_level: currentIssueLevel,
        data: { decision_update: updateData.decision_update, decision_completion_date: updateData.decision_comp_dt }
      });
      setOpen(false);
    }
  };

  useEffect(() => {
    setDisableSubmit(false);
    if (createIssueLogIsSuccess && !createIssueLogIsLoading) {
      setOpen(false);
      setSubmit(false);
      getIssueLog(studyId, selectedCountry, selectedSite, currentIssueLevel, pageNumber * pageSize, pageSize);
      handel();
      dispatchApp(
        setMessage({
          msgDetails: 'Issue created successfully',
          severity: 'success'
        })
      );
      dispatch(hideLoader());
    }
  }, [createIssueLogIsSuccess]);

  useEffect(() => {
    setDisableSubmit(false);
    if (
      (updateIssueLogIsSuccess && !updateIssueLogIsLoading) ||
      (updateAllIssueLogIsSuccess && !updateAllIssueLogIsLoading) ||
      (updateIssueActionIsSuccess && !updateIssueActionIsLoading) ||
      (updateIssueDesisionIsSuccess && !updateIssueDesisionIsLoading)
    ) {
      setOpen(false);
      setSubmit(false);
      getIssueLog(studyId, selectedCountry, selectedSite, currentIssueLevel, pageNumber * pageSize, pageSize);
      handel();
      dispatchApp(
        setMessage({
          msgDetails: 'Issue updated successfully',
          severity: 'success'
        })
      );
    }
    if (
      createIssueLogIsError ||
      updateIssueLogIsError ||
      updateAllIssueLogIsError ||
      updateIssueActionIsError ||
      updateIssueDesisionIsError
    ) {
      const err: any =
        createIssueLogError ||
        updateIssueLogError ||
        updateAllIssueLogError ||
        updateIssueActionError ||
        updateIssueDesisionError;
      if (err?.status == 500) {
        dispatch(
          setMessage({
            msgDetails: `${err?.status} : Internal Server Error`,
            severity: 'error'
          })
        );
      } else if (err?.status == 403) {
        dispatch(
          setMessage({
            msgDetails: 'Error 403 Restricted User permission',
            severity: 'error'
          })
        );
      } else if (err?.status == 404) {
        dispatch(
          setMessage({
            msgDetails: 'Error in updating issue(s)',
            severity: 'error'
          })
        );
      }
    }
    dispatch(hideLoader());
  }, [
    updateIssueLogIsSuccess,
    updateAllIssueLogIsSuccess,
    updateIssueActionIsSuccess,
    updateIssueDesisionIsSuccess,
    createIssueLogIsError,
    updateIssueLogIsError,
    updateAllIssueLogIsError,
    updateIssueActionIsError,
    updateIssueDesisionIsError,
    updateIssueLogIsLoading,
    updateAllIssueLogIsLoading,
    updateIssueActionIsLoading,
    updateIssueDesisionIsLoading
  ]);
  useEffect(() => {
    if (deleteIssueLogIsSuccess || deleteAllIssueLogIsSuccess) {
      dispatchApp(
        setMessage({
          msgDetails: 'Issue deleted successfully',
          severity: 'success'
        })
      );
    }
    if (deleteIssueLogIsError || deleteAllIssueLogIsError) {
      const err: any = deleteAllIssueLogError || deleteIssueLogError;
      if (err?.status == 500) {
        dispatch(
          setMessage({
            msgDetails: `${err?.status} : Internal Server Error`,
            severity: 'error'
          })
        );
      } else if (err?.status == 403) {
        dispatch(
          setMessage({
            msgDetails: 'Error 403 Restricted User permission',
            severity: 'error'
          })
        );
      } else if (err?.status == 404) {
        dispatch(
          setMessage({
            msgDetails: 'Error in deleteing issue(s)',
            severity: 'error'
          })
        );
      }
    }
    dispatch(hideLoader());
  }, [
    deleteIssueLogData,
    deleteIssueLogError,
    deleteIssueLogIsError,
    deleteIssueLogIsSuccess,
    deleteAllIssueLogData,
    deleteAllIssueLogError,
    deleteAllIssueLogIsError,
    deleteAllIssueLogIsSuccess
  ]);

  const handelEdit = (data: any) => {
    setUpdateData(data);
    setEdit(true);
    setOpen(true);
  };
  const handleDeleteOpen = (data: any) => {
    setDeleteData({ ...data, application: 'CTMS' });
    setOpenDelete(true);
  };

  const handelBack = () => {
    setIssuesOn(false);
    setStudyLogOpen(true);
  };
  const handleDeleteCancel = () => {
    setOpenDelete(false);
  };
  const handleDelete = async () => {
    setOpenDelete(false);
    let deleteResponse: any = {};
    if (deleteData?.created_by === user) {
      deleteResponse = await deleteIssueLog({
        issue_id: updateData.issue_id,
        site_id: selectedSite,
        study_id: studyId,
        country: selectedCountry,
        application: 'CTMS',
        issue_level: currentIssueLevel,
        data: deleteData
      });
    } else {
      deleteResponse = await deleteAllIssueLog({
        issue_id: updateData.issue_id,
        site_id: selectedSite,
        study_id: studyId,
        country: selectedCountry,
        application: 'CTMS',
        issue_level: currentIssueLevel,
        data: deleteData
      });
    }
    if (!deleteResponse?.error) {
      // update the issue and refetching the getIssueLog
      await getIssueLog(studyId, selectedCountry, selectedSite, currentIssueLevel, pageNumber * pageSize, pageSize);
    }
    dispatch(hideLoader());
  };

  useEffect(() => {
    if (createData.action_taken || updateData.action_taken) {
      setAtError(false);
    }
    if (createData.action_completion_dt || updateData.action_completion_dt) {
      setDaError(false);
    }
    if (createData.decision_update || updateData.decision_update) {
      setDuError(false);
    }
    if (createData.decision_comp_dt || updateData.decision_comp_dt) {
      setDdError(false);
    }
    if (createData.dt_occur || updateData.dt_occur) {
      setIaError(false);
    }
  }, [
    createData.action_taken,
    createData.action_completion_dt,
    createData.decision_update,
    createData.decision_comp_dt,
    createData.dt_occur,
    updateData.action_taken,
    updateData.action_completion_dt,
    updateData.decision_update,
    updateData.decision_comp_dt,
    updateData.dt_occur
  ]);

  const mapPageToNextCursor = React.useRef<{ [page: number]: GridRowId }>({});

  const handlePageChange = (newPage: number) => {
    // We have the cursor, we can allow the page transition.
    if (newPage === 0 || mapPageToNextCursor.current[newPage - 1]) {
      setPageNumber(newPage);
    }
  };

  const SERVER_OPTIONS = {
    useCursorPagination: true
  };

  const { useQuery } = createFakeServer({}, SERVER_OPTIONS);

  const queryOptions = React.useMemo(
    () => ({
      cursor: mapPageToNextCursor.current[pageNumber - 1],
      pageSize
    }),
    [pageNumber]
  );
  const { isLoading, pageInfo } = useQuery(queryOptions);

  React.useEffect(() => {
    if (!isLoading && pageInfo?.nextCursor) {
      // We add nextCursor when available
      mapPageToNextCursor.current[pageNumber] = pageInfo?.nextCursor;
    }
  }, [pageNumber, isLoading, pageInfo?.nextCursor]);

  const handel = () => {
    setPageNumber(0);
  };

  function TabPanel(props: { [x: string]: any; children: any; value: any; index: any }) {
    const { children, value, index, ...other } = props;

    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`simple-tabpanel-${index}`}
        aria-labelledby={`simple-tab-${index}`}
        {...other}
      >
        {value === index && (
          <Box sx={{ p: 3 }}>
            <Typography>{children}</Typography>
          </Box>
        )}
      </div>
    );
  }

  TabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired
  };
  function a11yProps(index: number) {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`
    };
  }

  const handleTabsChange = (_: React.SyntheticEvent, newValue: number) => {
    setRowsCount(0);
    setPageNumber(0);
    setPageSize(10);
    setValue(newValue);
    setRows([]);
    newValue === 0 ? setIssueLevel('SITE') : setIssueLevel('SPONSOR');
  };
  const [issueAssigne, setIssueAssigne] = useState<any>([]);
  const [decisionAssigne, setDecisionAssigne] = useState<any>([]);
  const actionPermission = currentIssueLevel === 'SITE' ? 'UPDATE_SITE_ISSUE_ACTION' : 'UPDATE_SPONSOR_ISSUE_ACTION';
  const decisionPermission =
    currentIssueLevel === 'SITE' ? 'UPDATE_SITE_ISSUE_DECISION' : 'UPDATE_SPONSOR_ISSUE_DECISION';
  const [
    getAssignees,
    { data: assignees, isError: isAssingeeError, isSuccess: isAssingeeSuccess, isLoading: isAssingeeLoading }
  ] = useGetAssigneesMutation();

  const [
    getDecisionAssignees,
    {
      data: decisionAssignees,
      isError: isDecisionAssingeeIsError,
      isSuccess: isDecisionAssingeeSuccess,
      isLoading: isDecisionAssingeeLoading
    }
  ] = useGetAssigneesMutation();

  useEffect(() => {
    if (!isAssingeeLoading && isAssingeeError) {
      dispatch(setMessage({ msgDetails: 'Unable to get assignees', severity: 'error' }));
    } else if (!isAssingeeLoading && isAssingeeSuccess && assignees) {
      const assignList: any = [];
      assignees.map((item: any) =>
        assignList.push({
          key: `${item.first_name} ${item.last_name}`,
          displayName: `${item.first_name} ${item.last_name} (${item.email})`
        })
      );
      setIssueAssigne(assignList);
    }
  }, [isAssingeeLoading, isAssingeeSuccess, assignees, isAssingeeError]);

  useEffect(() => {
    if (isDecisionAssingeeIsError && !isDecisionAssingeeLoading) {
      dispatch(setMessage({ msgDetails: 'Unable to get assignees', severity: 'error' }));
    } else if (!isDecisionAssingeeLoading && isDecisionAssingeeSuccess && decisionAssignees) {
      const assignList: any = [];
      decisionAssignees.map((item: any) =>
        assignList.push({
          key: `${item.first_name} ${item.last_name}`,
          displayName: `${item.first_name} ${item.last_name} (${item.email})`
        })
      );
      setDecisionAssigne(assignList);
    }
  }, [decisionAssignees, isDecisionAssingeeIsError, isDecisionAssingeeSuccess, isDecisionAssingeeLoading]);

  useEffect(() => {
    getAssignees({
      client_name: 'ctms-ui',
      data: [actionPermission]
    });
    getDecisionAssignees({
      client_name: 'ctms-ui',
      data: [decisionPermission]
    });
  }, [currentIssueLevel, value]);

  const actionUsersList: any = [];

  issueAssigne?.map((data: any) => {
    actionUsersList.push(data.displayName);
  });

  const decisionUserList: any = [];

  decisionAssigne?.map((data: any) => {
    decisionUserList.push(data.displayName);
  });

  const handleSelectionModelChange = async (ids: any) => {
    setSelectionModel(ids);
    const selectedIDs = new Set(ids);
    const selectedRows = rows
      .filter((row: any) => selectedIDs.has(row.id))
      .map((elem: any) => elem.issue_no)
      .sort();

    setRowSelection(selectedRows);
  };

  const handleOpenCopyToSponsor = async () => {
    if (rowSelection.length > 0) {
      dispatch(showLoader());
      const tempSponsorData: any = await getIssueLogData({
        data: { ...filterObject },
        site_id: selectedSite,
        study_id: studyId,
        country: selectedCountry,
        issue_level: 'SPONSOR',
        application: archLevel === 'site' ? process.env.REACT_APP_TSDA_SLUG : process.env.REACT_APP_NAME
      });
      if (tempSponsorData?.isSuccess) {
        const sponsorList =
          tempSponsorData.data && tempSponsorData.data.selected_data.length > 0
            ? tempSponsorData?.data?.selected_data
                ?.filter((item: any) => item.issue_details.cloned_issue_no !== null)
                .map((item: any) => item.issue_details.cloned_issue_no)
            : [];
        const copiedList = rowSelection.filter((elem: any) => sponsorList.includes(elem));
        const unCopiedIssues = rowSelection.filter((elem: any) => !sponsorList.includes(elem));
        if (unCopiedIssues.length === 0) {
          dispatch(
            setMessage({
              msgDetails: 'Selected issue(s) already copied to Sponsor',
              severity: 'error'
            })
          );
          setOpenCopyToSponsor(false);
        } else {
          setOpenCopyToSponsor(true);
          if (copiedList.length !== 0) {
            dispatch(
              setMessage({
                msgDetails: 'Some of the selected issues are already copied to Sponsor are excluded from copy list',
                severity: 'error'
              })
            );
          }
          setRowSelection(unCopiedIssues);
        }
      } else if (tempSponsorData?.isError) {
        setRowSelection(rowSelection);
        setOpenCopyToSponsor(true);
      }
    }
    dispatch(hideLoader());
  };
  const handleCloseCopyToSponsor = () => {
    setOpenCopyToSponsor(false);
  };
  const handleConfirmCopyToSponsor = async () => {
    await copyToSponsor({
      site_id: selectedSite,
      study_id: studyId,
      country: selectedCountry,
      data: { issue_no_list: rowSelection },
      application: 'CTMS'
    });
  };

  useEffect(() => {
    if (copyToSponsorIsLoading) {
      dispatch(showLoader());
    } else {
      dispatch(hideLoader());
    }
  }, [copyToSponsorIsLoading]);

  useEffect(() => {
    if (copyToSponsorIsSuccess) {
      dispatchApp(
        setMessage({
          msgDetails: 'Issue(s) created successfully',
          severity: 'success'
        })
      );
      setOpenCopyToSponsor(false);
      setSelectionModel([]);
      setIssueLevel(`SPONSOR`);
      setValue(1);
    } else if (copyToSponsorIsError) {
      const err: any = copyToSponsorError;
      if (err?.status == 500) {
        dispatch(
          setMessage({
            msgDetails: `${err?.status} : Internal Server Error`,
            severity: 'error'
          })
        );
      } else if (err?.status == 403) {
        dispatch(
          setMessage({
            msgDetails: 'Error 403 Restricted User permission',
            severity: 'error'
          })
        );
      } else if (err?.status == 404) {
        dispatch(
          setMessage({
            msgDetails: 'Error in copy to sponsor issue(s)',
            severity: 'error'
          })
        );
      }
    }
    const errorCopy: any = copyToSponsorError;
    const unCopiedIssues = rowSelection.filter(
      (item: any) => !errorCopy?.data?.context?.duplicate_issue_no.includes(item)
    );
    setRowSelection(unCopiedIssues);
  }, [copyToSponsorIsSuccess, copyToSponsorIsError, copyToSponsorData, copyToSponsorError]);

  // for Custom buttons at bottom in Data Grid
  const CustomFooterButtons = () => {
    return (
      <GridFooterContainer>
        <Box sx={{ px: 1, display: 'flex', columnGap: 1 }}>
          {issueLogPermissions.update ? (
            <Button
              startIcon={<AddIcon />}
              onClick={handleClickOpen}
              disabled={!((currentIssueLevel == 'SITE' && selectedSite != null) || currentIssueLevel !== 'SITE')}
            >
              ADD ISSUE
            </Button>
          ) : (
            ''
          )}
          {copyToSponsorIssuePermission &&
          viewSponsorIssuePermission &&
          currentIssueLevel === 'SITE' &&
          selectedSite !== null ? (
            <Button
              variant="contained"
              disabled={selectionModel.length === 0 ? true : false}
              onClick={handleOpenCopyToSponsor}
              sx={{ letterSpacing: '-0.25px', padding: '6px 8px' }}
            >
              COPY AS NEW SPONSOR ISSUE
            </Button>
          ) : null}
        </Box>
        <GridFooter />
      </GridFooterContainer>
    );
  };

  return (
    <div>
      <Loader />
      <Box className="mainBackBtnWrap">
        <Button onClick={handelBack} startIcon={<ArrowBackIcon fontSize="small" />}>
          LIST OF LOGS
        </Button>
      </Box>
      <Grid container spacing={2} alignItems="center" className="titleGrid">
        <Grid item xs>
          <Stack direction="row" spacing={4} alignItems="center">
            <Typography variant="h6" component="h6" sx={{ height: '35px' }}>
              Issues
            </Typography>
          </Stack>
        </Grid>
        <Grid item sx={{ textAlign: 'right' }}>
          <Button variant="contained" style={{ textTransform: 'none' }}>
            {value ? 'PUBLISH TO eTMF' : 'PUBLISH TO eISF'}
          </Button>
        </Grid>
      </Grid>
      <Box sx={{ width: '100%' }}>
        {archLevel == 'site' && archLevel != 'sponsor' && viewSiteIssuePermission ? (
          <Tabs
            value={value}
            onChange={handleTabsChange}
            aria-label="Subject Tracking Tabs"
            className="tabsFontSize extendedTabs"
          >
            <Tab label="Site" {...a11yProps(0)} />
          </Tabs>
        ) : (
          ''
        )}
        {archLevel == 'sponsor' && archLevel != 'site' ? (
          <Tabs
            value={value}
            onChange={handleTabsChange}
            aria-label="Subject Tracking Tabs"
            className="tabsFontSize extendedTabs"
          >
            {viewSiteIssuePermission && <Tab label="Site" {...a11yProps(0)} />}
            {viewSponsorIssuePermission && <Tab label="Sponsor" {...a11yProps(1)} />}
          </Tabs>
        ) : (
          ''
        )}
        <DataGridIssueLog
          rows={rows}
          rowsCount={rowsCount}
          page={pageNumber}
          pageSize={pageSize}
          setPageSize={setPageSize}
          apiRef={apiRef}
          handlePageChange={handlePageChange}
          handleClickOpen={handleClickOpen}
          handleDeleteOpen={handleDeleteOpen}
          handelEdit={handelEdit}
          isLoading={dataGridShow}
          CustomFooterButtons={CustomFooterButtons}
          issueLogPermissions={issueLogPermissions}
          currentIssueLevel={currentIssueLevel}
          handleSelectionModelChange={handleSelectionModelChange}
          selectionModel={selectionModel}
          selectedSite={selectedSite}
          columnVisibilityModel={
            currentIssueLevel === 'SITE' ? columnVisibilityModelSite : columnVisibilityModelSponsor
          }
          setColumnVisibilityModel={
            currentIssueLevel === 'SITE' ? setColumnVisibilityModelSite : setColumnVisibilityModelSponsor
          }
          copyToSponsorIssuePermission={copyToSponsorIssuePermission}
          pageNumber={pageNumber}
          setPageNumber={setPageNumber}
          setFilterObject={setFilterObject}
        />
      </Box>
      <PopupComponent aria-labelledby="customized-dialog-title" open={open} maxWidth="md">
        <Loader />
        <DialogTitle>{edit ? 'Edit' : 'Add'} Issue</DialogTitle>
        <DialogContent className="modal800">
          <AddIssueForm
            createData={createData}
            setCreateData={setCreateData}
            issueData={rows}
            edit={edit}
            updateData={updateData}
            setUpdateData={setUpdateData}
            category={category}
            issue={issue}
            serverity={serverity}
            initialState={initialState}
            submit={submit}
            componentRef={componentRef}
            error={error}
            atError={atError}
            daError={daError}
            duError={duError}
            ddError={ddError}
            iaError={iaError}
            issueLogPermissions={issueLogPermissions}
            decisionUserList={decisionUserList}
            actionUsersList={actionUsersList}
            currentIssueLevel={currentIssueLevel}
            setDisableSubmit={setDisableSubmit}
            setWarningMessage={setWarningMessage}
          />
        </DialogContent>
        <DialogActions>
          <Button className="grey-btn" onClick={handleClose}>
            Close
          </Button>
          <Button onClick={handleSave} ref={buttonRef} disabled={disableSubmit}>
            Save
          </Button>
        </DialogActions>
      </PopupComponent>
      <div>
        <PopupComponent aria-labelledby="customized-dialog-title" open={openDelet} maxWidth="lg">
          <DialogTitle>Delete Issue?</DialogTitle>
          <DialogContent>
            <Typography variant="body1">Are you sure you want to delete {deleteData?.issue_no} issue?</Typography>
          </DialogContent>
          <DialogActions>
            <Button className="grey-btn" onClick={handleDeleteCancel}>
              No
            </Button>
            <Button onClick={handleDelete}>Yes</Button>
          </DialogActions>
        </PopupComponent>
        <PopupComponent
          open={openConfirmExit}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          className="confirm-modal"
          child
        >
          <DialogTitle id="alert-dialog-title">
            <b>Confirm Exit</b>
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">{unSavedWarning}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setOpenConfirmExit(false);
              }}
              className="grey-btn"
            >
              CLOSE
            </Button>
            <Button
              onClick={() => {
                setOpen(false);
                setOpenConfirmExit(false);
              }}
            >
              OK
            </Button>
          </DialogActions>
        </PopupComponent>
      </div>
      {/* {rowSelection.length > 0 ? ( */}

      <PopupComponent open={openCopyToSponsor}>
        <Loader />
        <DialogTitle>Confirm</DialogTitle>
        <DialogContent className="tsda_prescreenDialog">
          <DialogContentText>
            <Typography mt={2} mb={1} sx={{ color: 'rgba(0,0,0,0.87)' }}>
              Would you like to copy the following issue(s) to Sponsor issues?
            </Typography>
          </DialogContentText>

          <Paper sx={{ width: '100%', height: '196px', bgcolor: 'background.paper', overflowY: 'auto', mb: 2 }}>
            <List dense component="nav">
              {rowSelection.map((issueNo: any, i: number) => (
                <ListItem key={i} disablePadding>
                  <ListItemText primary={issueNo} sx={{ padding: '5px', paddingLeft: '13px' }} />
                </ListItem>
              ))}
            </List>
          </Paper>
          <DialogActions>
            <Button className="grey-btn" onClick={handleCloseCopyToSponsor}>
              Cancel
            </Button>
            <Button onClick={handleConfirmCopyToSponsor} disabled={rowSelection.length === 0}>
              Confirm
            </Button>
          </DialogActions>
        </DialogContent>
      </PopupComponent>

      {/* ) : null} */}
    </div>
  );
};

export default React.memo(Issues);
